import type { ISegment } from "@repo/baseCloud";

import { MachinesDropdown, RepsDropdown, SegmentFilters } from "@repo/baseCloud/components";
import { Checkbox, DateTimePicker } from "@repo/ui";
import { useCallback } from "react";

import { useMapSettings } from "../hooks";
import { useMapFilters } from "./filterContext";

type FilterType = "Rep" | "Machine";

interface FilterProps {
	filterType: FilterType;
}

export function RepresentativesFilters(props: FilterProps) {
	const { filters, setFilters, filterChanged } = useMapFilters();
	const mapSettingsQuery = useMapSettings();

	const segmentChanged = useCallback(
		(segFilters: ISegment) => setFilters((prev) => ({ ...prev, ...segFilters })),
		[setFilters],
	);

	return (
		<>
			<SegmentFilters onChange={segmentChanged} value={filters} />
			{props.filterType === "Rep" && (
				<RepsDropdown value={filters.reps} changeHandler={filterChanged} filters={filters} />
			)}
			{props.filterType === "Machine" && (
				<MachinesDropdown value={filters.machines} changeHandler={filterChanged} filters={filters} />
			)}

			{props.filterType === "Rep" && (
				<Checkbox
					id="activeRepOnly"
					label={`Active Reps Only`}
					value={filters.activeRepOnly ?? false}
					changeHandler={filterChanged}
					inline
				/>
			)}
			{props.filterType === "Machine" && (
				<Checkbox
					id="activeMachineOnly"
					label={`Active Machines Only`}
					value={filters.activeMachineOnly ?? false}
					changeHandler={filterChanged}
					inline
				/>
			)}
			<DateTimePicker
				id="startDateTime"
				label="Start Date"
				value={filters.startDateTime}
				changeHandler={filterChanged}
			/>
			<DateTimePicker
				id="endDateTime"
				label="End Date"
				value={filters.endDateTime}
				changeHandler={filterChanged}
			/>
			<Checkbox
				id="showSales"
				label={"Show Sales"}
				value={filters.showSales ?? false}
				changeHandler={filterChanged}
				inline
				hidden={!mapSettingsQuery.data?.canShowSales}
			/>
			<Checkbox
				id="goodSales"
				label={"Show Good Sales Only"}
				value={filters.goodSales ?? false}
				changeHandler={filterChanged}
				inline
				hidden={!filters.showSales}
			/>
			<Checkbox
				id="leads"
				label={"Show Leads"}
				value={filters.leads ?? false}
				changeHandler={filterChanged}
				inline
				hidden={!mapSettingsQuery.data?.canShowLeads}
			/>
			<Checkbox
				id="convertedLeads"
				label={"Converted Leads Only"}
				value={filters.convertedLeads ?? false}
				changeHandler={filterChanged}
				inline
				hidden={!filters.leads}
			/>
			<Checkbox
				id="positions"
				name="positions"
				label={"Show Positions"}
				value={filters.positions ?? false}
				changeHandler={filterChanged}
				inline
				hidden={!mapSettingsQuery.data?.canShowPositions}
			/>
			<Checkbox
				id="showLines"
				label={"Show Route Paths"}
				value={filters.showLines}
				changeHandler={filterChanged}
				inline
				hidden={!filters.positions}
			/>
			<Checkbox
				id="retailLocations"
				label={"Show Retail Locations"}
				value={filters.retailLocations ?? false}
				changeHandler={filterChanged}
				inline
				hidden={!mapSettingsQuery.data?.canShowRetailLocations}
			/>
		</>
	);
}
